import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import { ConsoleLog } from '../../../modules/logging'
import MainModule from '../../../modules/main'
import { getCardPlaceholder, isObjectEmpty } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'create-event-first-recap', {
  title: "Résumé de l'événement",
  expositionTitle: 'Résumé de l’exposition',
  festivalTitle: 'Résumé du festival',
  description:
    'Vous y êtes presque !  Si vous souhaitez modifier ou ajouter une représentation, cliquez sur “Modifier”. Si vous avez terminé, cliquez sur “Soumettre” pour envoyer le tout en validation par les équipes internes de La Vitrine.',
  expositionDescription:
    'Vous y êtes presque !  Si vous souhaitez modifier ou ajouter une représentation, cliquez sur “Modifier”. Si vous avez terminé, cliquez sur “Soumettre” pour envoyer le tout en validation par les équipes internes de La Vitrine.',
  festivalDescription:
    'Vous y êtes presque ! Si vous souhaitez modifier votre festival, cliquez sur "Retour" pour rejoindre l\'écran aillant besoin de modifications. Si vous avez terminé, cliquez sur "Soumettre" pour envoyer le tout en validation par les équipes internes de La Vitrine.',
  existingEventDescription:
    "Veuillez prendre connaissance des informations fournies sur l'événement pour confirmer que c'est bien cet événement auquel vous voulez ajouter une ou des représentation(s).",
  existingFestivalDescription:
    "Veuillez prendre connaissance des informations fournies sur le festival pour confirmer qu'il s'agit bien du festival auquel vous voulez ajouter une ou des représentation(s) ou exposition(s).",
  confirmExpositionDescription: `Veuillez prendre connaissance des informations fournies sur l'exposition et cliquer sur "Suivant" pour l'ajouter à votre festival.`,
  confirmEventDescription:
    "Veuillez prendre connaissance des informations fournies sur l'événement et sélectionner les représentations que vous souhaitez ajouter à votre festival.",
  addRepresentation: 'Ajouter une autre représentation',
  noOffersChosen: 'Vous devez choisir au moins une offre',
})

Locale.getInstance().registerKeySet('en', 'create-event-first-recap', {
  title: 'Event Recap',
  expositionTitle: 'Exposition Recap',
  festivalTitle: 'Festival Recap',
  description:
    "You're almost there!  If you wish to modify or add a representation, click on “Modify”. When you're finished, click on “Submit” to send the whole thing to La Vitrine's in-house teams for validation.",
  expositionDescription:
    "You're almost there!  If you wish to modify or add a representation, click on “Modify”. When you're finished, click on “Submit” to send the whole thing to La Vitrine's in-house teams for validation.",
  festivalDescription:
    "You're almost there! If you wish to modify your festival, click on “Back” to reach the screen that requires changes. When you're finished, click on “Submit” to send the whole thing to La Vitrine's in-house teams for validation.",
  existingEventDescription:
    'Please review the information provided about the event and confirm that this is indeed the event to which you want to add one or more showing(s).',
  existingFestivalDescription:
    'Please review the information provided about the festival and confirm that this is indeed the festival to which you want to add one or more showing(s) or exhibition(s).',
  confirmExpositionDescription: `Please ensure that this exhibition is the one you were looking for and, then, click "Next" to add it to your festival.`,
  confirmEventDescription:
    'Please ensure that this event is the one you were looking for and, then, select the showings you wish to add to your festival.',
  addRepresentation: 'Add another representation',
  noOffersChosen: 'You have to choose at least one offer',
})

export interface LvcCreateEventFinalRecap {
  flowGroup: string
  flowStep: string
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
  representations: any[]
  existingRepresentations: any[]
  dataConfirmationMode: boolean
  selectedOffers: any[]
}

export interface LvcCreateEventFinalRecapData {
  eventName: string
  eventImage: string
  eventDescription: string
  eventDisciplines: string
  representations: any[]
}

export class LvcCreateEventFinalRecap
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private core: IFlowCore | undefined
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
    ['data-confirmation-mode', false],
    ['data-adding-events', false],
  ]

  reactive: Reactive = [
    ['eventImage', ''],
    ['eventImagePath', ''],
    ['eventName', ''],
    ['eventDescription', ''],
    ['eventDisciplines', ''],
    ['eventFinalRecapData', {}],
    ['representations', []],
    ['existingRepresentations', []],
    ['selectedOffers', []],
    ['eventType', ''],
  ]

  handleModalEvent = (event: any) => {
    this.eventName = event.detail
    ConsoleLog(true, this.eventName)
  }

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)

    ConsoleLog(true, 'this.flowGroup : ', this.flowGroup)
    this.core = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  onSelectOffer(event: Event) {
    const eventCard = (event.target as Element)?.closest('.select-offer') as Element | null
    if (!eventCard) return
    const data = eventCard.getAttribute('data-value')
    if (!data) return
    const offer = JSON.parse(decodeURIComponent(data))
    const selectedOffers = !this.selectedOffers?.length ? [] : this.selectedOffers || []

    const index = selectedOffers.findIndex(selectedOffer => selectedOffer.id === offer.id)
    if (index !== -1) {
      selectedOffers.splice(index, 1)
    } else {
      selectedOffers.push(offer)
    }
    this.selectedOffers = selectedOffers

    // Get the FlowCore instance to save the data
    const flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    if (flowCore) {
      flowCore.save()
    } else {
      console.warn('FlowCore instance not found, Saving aborted.')
    }

    this.rerender()
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    const entireData = data
    ConsoleLog(true, 'entireEventData:', entireData)

    // Set existing representations and event type based on flow step
    if (this.flowStep === 'addedEventConfirmation') {
      this.existingRepresentations = entireData?.addedEventConfirmation?.offers || []
      this.eventType = entireData?.addedEventConfirmation?.data?.table
    } else {
      this.existingRepresentations =
        entireData?.createEventSearchOrAdd?.existingRepresentations || []
    }

    // Determine the event data source based on flow step
    const eventData =
      this.flowStep === 'addedEventConfirmation'
        ? entireData?.addedEventConfirmation?.data
        : entireData?.createEventSearchOrAdd?.eventData

    // Process eventData if present, otherwise fall back on createEventAddImage & createEventDescription
    if (!isObjectEmpty(eventData)) {
      if (eventData?.createEventAddImage?.imageUrl) {
        const imageUrl = eventData.createEventAddImage.imageUrl
        this.eventImagePath = imageUrl
        this.eventImage = await MainModule.getInstance().getAuth().getImageUrl(imageUrl)
      }

      // Check for a thumbnail image override in medias
      const thumbnailData = eventData?.medias?.[0]?.media
      if (thumbnailData) {
        try {
          const mediaObj = JSON.parse(decodeURIComponent(thumbnailData))
          const thumbUrl = mediaObj?.[0]?.url
          if (thumbUrl) {
            this.eventImagePath = thumbUrl
            this.eventImage = thumbUrl
          } else {
            // Use a placeholder if thumbnail URL is not found
            const placeholder = getCardPlaceholder(eventData?.type)
            this.eventImagePath = placeholder
            this.eventImage = placeholder
          }
        } catch (error) {
          console.error('Error parsing thumbnail data:', error)
        }
      } else if (!this.eventImage) {
        // If no thumbnail, use placeholder based on event type
        const placeholder = getCardPlaceholder(eventData?.type)
        this.eventImagePath = placeholder
        this.eventImage = placeholder
      }

      // Set event name and description based on current locale
      const langKey = Locale.language === 'fr' ? 'nameFr' : 'nameEn'
      const descKey = Locale.language === 'fr' ? 'descriptionFr' : 'descriptionEn'
      this.eventName = eventData?.[langKey]
      this.eventDescription = eventData?.[descKey]

      // Representations come from addRepresentation block
      this.representations = entireData?.addRepresentation?.representations
    } else {
      // Fallback when eventData is empty: use createEventAddImage and createEventDescription data
      const addImageUrl = entireData?.createEventAddImage?.imageUrl
      if (addImageUrl) {
        this.eventImagePath = addImageUrl
        this.eventImage = await MainModule.getInstance().getAuth().getImageUrl(addImageUrl)
      }

      const eventDesc = entireData?.createEventDescription
      if (eventDesc) {
        this.eventName = eventDesc[Locale.language === 'fr' ? 'eventNameFr' : 'eventNameEn']
        this.eventDescription =
          eventDesc[Locale.language === 'fr' ? 'eventDescriptionFr' : 'eventDescriptionEn']
        this.eventDisciplines = eventDesc.disciplines
      }

      this.representations = entireData?.addRepresentation?.representations
    }

    // For festivals at the final event recap step, override representations with selected offers.
    if (this.dataType === 'festival' && this.flowStep === 'finalEventRecap') {
      this.representations = entireData?.selectedOffers || []
    }

    // Process any event contributors if available
    const existingContributors =
      eventData?.artists?.map((artist: any) => ({
        ...artist,
        contributorName: artist[Locale.language === 'fr' ? 'nameFr' : 'nameEn'],
      })) || []

    // final recap data object
    this.eventFinalRecapData = {
      image: this.eventImage,
      imagePath: this.eventImagePath,
      eventName: this.eventName,
      eventDescription: this.eventDescription,
      eventDisciplines: this.eventDisciplines,
      eventContributors:
        entireData.addEventContributors?.contributors || existingContributors || [],
    }

    ConsoleLog(true, 'recap data:', this.eventFinalRecapData)
    this.rerender()
  }

  renderRepresentation() {
    if (this.representations?.length || this.existingRepresentations?.length) {
      ConsoleLog(true, 'this.representations when rendering block : ', this.representations)
      return `
          ${
            this.existingRepresentations.length
              ? `
            <lvc-text font-preset="medium">
              ${Locale.getKey(
                this.flowStep === 'addedEventConfirmation'
                  ? 'lvc-create-event-representations.chooseRepresentationsLabel'
                  : this.flowGroup === 'festivalCreation'
                    ? 'lvc-create-event-representations.existingRepresentationsAndExhibitionsLabel'
                    : 'lvc-create-event-representations.existingRepresentationsLabel'
              )}
            </lvc-text>
          `
              : ''
          }
          ${this.existingRepresentations
            .map((representation: any, index: number) => {
              return `
              <div class="representation-block">
                <lvc-representation-block
                  representation-data="${encodeURIComponent(JSON.stringify(representation))}"
                  representation-index="${index}"
                  flow-group="${this.flowGroup}"
                  is-existing="true"
                  show-type="${this.flowGroup === 'festivalCreation'}"
                ></lvc-representation-block>
                ${
                  this.dataAddingEvents
                    ? `
                  <button
                    class="select-offer"
                    data-value="${encodeURIComponent(JSON.stringify(representation))}"
                  >
                    ${
                      this.selectedOffers.find(offer => offer.id === representation.id)
                        ? '<lvc-close-icon width="20" height="20"></lvc-close-icon>'
                        : `
                        <lvc-add-icon class="add-offer-icon" data-color="#1E007D"></lvc-add-icon>
                      `
                    }
                  </button>
                `
                    : ''
                }
              </div>
            `
            })
            .join('')}
          ${
            this.representations?.length
              ? `
            <lvc-text font-preset="medium">
              ${Locale.getKey(
                this.flowGroup === 'festivalCreation'
                  ? 'lvc-create-event-representations.addedRepresentationsAndExhibitionsLabel'
                  : 'lvc-create-event-representations.addedRepresentationsLabel'
              )}
            </lvc-text>
          `
              : ''
          }
          ${
            this.representations?.length
              ? this.representations
                  ?.map((representation: any, index: number) => {
                    return `
              <lvc-representation-block
                representation-data="${encodeURIComponent(JSON.stringify(representation))}"
                representation-index="${index}"
                flow-group="${this.flowGroup}"
                is-existing="${this.dataType === 'festival' && this.flowStep === 'finalEventRecap'}"
                show-type="${this.flowGroup === 'festivalCreation'}"
              ></lvc-representation-block>
            `
                  })
                  .join('')
              : ''
          }
        `
    }
    return ''
  }

  public async serialize(): Promise<unknown> {
    if (this.dataAddingEvents) {
      const data = this.core?.getData()
      const isExhibition = data.addedEventConfirmation?.data?.table === 'exhibitionEvents'
      const selectedOffers = data.selectedOffers || []
      const updatedOffers = isExhibition
        ? [...selectedOffers, data.addedEventConfirmation?.data]
        : [...selectedOffers, ...this.selectedOffers]
      const uniqueOffers = updatedOffers.filter(
        (offer, index, self) =>
          index === self.findIndex(o => o.id === offer.id && o.table === offer.table)
      )
      this.core?.setStepData('selectedOffers', uniqueOffers)
      this.core?.setStepData('addedEventConfirmation', null)
      return {
        eventFinalRecapData: this.eventFinalRecapData,
        representations: this.representations,
        existingRepresentations: this.existingRepresentations,
        selectedOffers: this.selectedOffers,
      }
    }
    return ''
  }

  public async validate(): Promise<boolean> {
    const data = this.core?.getData()
    if (this.dataAddingEvents) {
      const isExhibition = data.addedEventConfirmation?.data?.table === 'exhibitionEvents'
      if (!this.selectedOffers.length && !isExhibition) {
        MainModule.getInstance().showToastAlert(
          Locale.getKey('create-event-first-recap.noOffersChosen'),
          3000,
          true
        )
        return false
      }
    }
    return true
  }

  onContributorsButtonClick(): void {
    // go to contributors step
    this?.core?.goTo('addEventContributors')
  }

  html = () =>
    `<div class="container">


    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey(
          this.flowStep === 'addedEventConfirmation'
            ? this.eventType === 'exhibitionEvents'
              ? 'create-event-first-recap.expositionTitle'
              : 'create-event-first-recap.title'
            : this.dataType === 'festival' || this.flowGroup === 'festivalCreation'
              ? 'create-event-first-recap.festivalTitle'
              : this.dataType === 'event'
                ? 'create-event-first-recap.title'
                : 'create-event-first-recap.expositionTitle'
        )}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${
          this.dataConfirmationMode
            ? Locale.getKey(
                this.flowGroup === 'festivalCreation'
                  ? 'create-event-first-recap.existingFestivalDescription'
                  : 'create-event-first-recap.existingEventDescription'
              )
            : Locale.getKey(
                this.flowStep === 'addedEventConfirmation'
                  ? this.eventType === 'exhibitionEvents'
                    ? 'create-event-first-recap.confirmExpositionDescription'
                    : 'create-event-first-recap.confirmEventDescription'
                  : this.dataType === 'event'
                    ? 'create-event-first-recap.description'
                    : this.dataType === 'festival'
                      ? 'create-event-first-recap.festivalDescription'
                      : 'create-event-first-recap.expositionDescription'
              )
        }
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px">
      
      <lvc-creation-recap event-data="${encodeURIComponent(JSON.stringify(this.eventFinalRecapData))}"></lvc-creation-recap>
    </lvc-box>

    <lvc-box mt="24px">
      ${this.renderRepresentation()} 
    </lvc-box>
  

  </div>
    `

  eventListeners: EventListeners = [
    ['lvc-event-search-field', 'change', this.handleModalEvent.bind(this)],
    [
      'lvc-creation-recap',
      'lvc-event-recap:goToContributors',
      this.onContributorsButtonClick.bind(this),
    ],
    ['.select-offer', 'click', this.onSelectOffer.bind(this)],
  ]

  css = () => {
    return /*css*/ `

  .footer {
    display: flex;
    height: 60px;
    padding: 0 10px;
  }

  lvc-add-icon{
    display: flex;
    justify-content: center;
    margin-right: 8px;
  }

  .add-offer-icon {
    margin-right: 0;
  }

  .representation-block {
    position: relative;
  }

  .select-offer {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 6px;
    border-radius: 999px;
    border: 2px solid #1E007D;
    background: #F5F5F5;
    cursor: pointer;
  }

  .addContributor {
    color: #ffff;
    background: var(--Minuit, #1E007D);
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%; 
    display: flex;
    padding: 10px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 1 0;
    border: none;
    margin: 10px;
    cursor: pointer;
    border-radius: 999px;
    `
  }
}
